import { useEffect } from 'react'

import { useBreakpoint } from './useBreakpoint'

/**
 * A custom React hook that improves the desktop user experience for Ant Design
 * DatePicker and TimePicker components by streamlining confirmation behavior.
 *
 * 📌 Purpose
 * Automatically triggers the "OK" button of the AntD Date/Time picker when a user
 * selects a date or time and then clicks outside the dropdown — eliminating
 * the need to manually press "OK".
 *
 * 🚫 Mobile Behavior
 * This hook is explicitly disabled on mobile devices to avoid unintended behavior.
 * Native Ant Design behavior is preserved on mobile.
 *
 * 🧠 Background
 * Based on BuildPass user feedback, manually confirming selections on desktop felt
 * unnecessary and slowed down workflows. This hook simplifies that interaction.
 *
 * ⚙️ How It Works
 * - Observes mutations to detect `.ant-picker-dropdown` being added.
 * - On detection:
 *   - Finds the OK button.
 *   - Attaches click listeners to time/date cells to flag a pending commit.
 *   - Listens for global mousedown (desktop interaction).
 *   - If a commit is pending and interaction occurs outside, it auto-clicks "OK".
 *
 * 🧼 Cleanup
 * - Disconnects the MutationObserver on unmount.
 * - Removes global event listeners safely.
 *
 * ⚠️ Limitations
 * - Relies on Ant Design DOM structure, which may change in future updates.
 * - Assumes dropdowns are rendered into `document.body`.
 *
 * 🧩 Integration
 * Call this hook once inside any component that uses DatePicker or TimePicker.
 */

export const useAntDPickerAutoOk = () => {
  const isMobile = useBreakpoint('sm') // disables on small screens

  useEffect(() => {
    // Can't do a if statement isMobile then return because the hook somehow
    // disrupts how the time picker works on mobile

    if (!isMobile) {
      const observer = new MutationObserver(() => {
        const dropdowns = document.querySelectorAll('.ant-picker-dropdown')

        dropdowns.forEach((dropdown) => {
          if (!(dropdown instanceof HTMLElement)) return

          const okBtn = dropdown.querySelector(
            '.ant-picker-ok button'
          ) as HTMLButtonElement | null

          if (!okBtn) return

          let hasPendingCommit = false
          let currentDropdown: HTMLElement | null = dropdown

          const triggerCommitIfNeeded = () => {
            if (currentDropdown && hasPendingCommit) {
              const ok = currentDropdown.querySelector(
                '.ant-picker-ok button'
              ) as HTMLButtonElement
              if (ok) ok.click()

              hasPendingCommit = false
              currentDropdown = null
            }
          }

          const onInteractionOutside = (event: MouseEvent) => {
            if (!currentDropdown) return
            const target = event.target as Node

            if (!currentDropdown.contains(target) && hasPendingCommit) {
              setTimeout(triggerCommitIfNeeded, 0)
              removeAllEventListeners()
            }
          }

          const removeAllEventListeners = () => {
            document.removeEventListener('mousedown', onInteractionOutside)
          }

          const setUpAutoCommit = () => {
            hasPendingCommit = true
            currentDropdown = dropdown

            removeAllEventListeners()
            document.addEventListener('mousedown', onInteractionOutside)
          }

          // Attach click listeners to cells
          const timePanel = dropdown.querySelector('.ant-picker-time-panel')
          const timeCells =
            timePanel?.querySelectorAll('.ant-picker-time-panel-cell') || []
          timeCells.forEach((cell) => {
            cell.removeEventListener('click', setUpAutoCommit)
            cell.addEventListener('click', setUpAutoCommit)
          })

          const dateCells = dropdown.querySelectorAll('.ant-picker-cell')
          dateCells.forEach((cell) => {
            cell.removeEventListener('click', setUpAutoCommit)
            cell.addEventListener('click', setUpAutoCommit)
          })
        })
      })

      observer.observe(document.body, {
        childList: true,
        subtree: true,
      })

      return () => {
        observer.disconnect()
        document.removeEventListener('mousedown', () => {})
      }
    }
  }, [isMobile])
}
